import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Video, Phone, MapPin, Link2, Users, Copy, MoreVertical, Trash2, Pencil, GripVertical, ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";

const LOCATION_LABELS = {
  google_meet: { icon: Video, label: "Google Meet — Automatic" },
  teams: { icon: Video, label: "Microsoft Teams — Automatic" },
  zoom_manual: { icon: Video, label: "Zoom — Manual Link" },
  phone: { icon: Phone, label: "Phone Call" },
  in_person: { icon: MapPin, label: "In Person" },
  custom_link: { icon: Link2, label: "Custom Link" },
};

const defaultForm = () => ({
  name: "", description: "", color: "#E8712C",
  duration_minutes: 30, location_type: "google_meet", location_details: "",
  auto_confirm: true, visibility: "public", active: true,
  min_notice_minutes: null, max_advance_days: null, increment_minutes: null,
  buffer_before: null, buffer_after: null, daily_limit: null,
  allow_client_cancel: null, cancel_deadline_hours: null,
  allow_client_reschedule: null, reschedule_deadline_hours: null,
  custom_questions: [], assigned_user_ids: [],
  availability_mode: "inherit", custom_weekly_rules: null,
});

const WEEKDAYS = [
  { key: "0", label: "Mon" }, { key: "1", label: "Tue" }, { key: "2", label: "Wed" },
  { key: "3", label: "Thu" }, { key: "4", label: "Fri" }, { key: "5", label: "Sat" },
  { key: "6", label: "Sun" },
];
const EMPTY_WEEKLY = { "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [] };
function normalizeWeekly(w) {
  if (!w) return { ...EMPTY_WEEKLY };
  return { ...EMPTY_WEEKLY, ...w };
}

const _slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || "q";
const swap = (arr, i, j) => { const a = [...arr]; [a[i], a[j]] = [a[j], a[i]]; return a; };

export default function AppointmentTypesTab() {
  const { currentOrgId, currentOrg, user } = useAuth();
  const orgSlug = currentOrg?.slug || "";
  const [types, setTypes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm());
  // Meeting-provider capability of the currently connected accounts. Loaded
  // once per session; used to guard Teams / Google Meet selections so a
  // client never books a "Teams meeting" that can't actually generate a link.
  const [caps, setCaps] = useState({ teams_capable: false, google_connected: false, loaded: false });

  useEffect(() => { if (currentOrgId) load(); }, [currentOrgId]);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch("/integrations/calendar/connections");
        const cs = r.connections || [];
        const g = cs.find((c) => c.provider === "google");
        const o = cs.find((c) => c.provider === "outlook");
        setCaps({
          teams_capable: !!(o && o.teams_capable),
          outlook_connected: !!o,
          google_connected: !!g,
          loaded: true,
        });
      } catch { setCaps((c) => ({ ...c, loaded: true })); }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        apiFetch(`/scheduling/appointment-types?organization_id=${currentOrgId}`),
        apiFetch(`/scheduling/staff?organization_id=${currentOrgId}`),
      ]);
      setTypes(t.appointment_types || []);
      setStaff(s.staff || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const openNew = () => {
    setEditId(null);
    setForm({ ...defaultForm(), assigned_user_ids: user?.id ? [user.id] : [] });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      ...defaultForm(), ...t,
      assigned_user_ids: t.assigned_user_ids || [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      const body = { ...form, organization_id: currentOrgId };
      if (editId) {
        await apiFetch(`/scheduling/appointment-types/${editId}`, { method: "PATCH", body: JSON.stringify(body) });
        toast.success("Updated");
      } else {
        await apiFetch("/scheduling/appointment-types", { method: "POST", body: JSON.stringify(body) });
        toast.success("Created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const duplicate = async (t) => {
    try { await apiFetch(`/scheduling/appointment-types/${t.id}/duplicate`, { method: "POST" });
      toast.success("Duplicated"); load(); } catch (e) { toast.error(e.message); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Existing bookings will be preserved and the type archived.`)) return;
    try { await apiFetch(`/scheduling/appointment-types/${t.id}`, { method: "DELETE" });
      toast.success("Removed"); load(); } catch (e) { toast.error(e.message); }
  };
  const toggleActive = async (t) => {
    try { await apiFetch(`/scheduling/appointment-types/${t.id}`, {
      method: "PATCH", body: JSON.stringify({ organization_id: currentOrgId, name: t.name, active: !t.active })
    }); load(); } catch (e) { toast.error(e.message); }
  };
  const copyLink = (t) => {
    const url = `${window.location.origin}/book/${orgSlug}/${t.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#57534E]">Create and manage the meetings clients can book with you.</p>
        <Button data-testid="apt-type-new" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
          <Plus size={14} className="mr-1.5" /> Create Appointment Type
        </Button>
      </div>

      {loading ? <p className="text-sm text-[#A8A29E]">Loading…</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {types.length === 0 && (
            <div className="col-span-full bg-white border border-dashed border-[#E8E2D9] rounded-3xl p-10 text-center">
              <p className="text-sm text-[#57534E]">No appointment types yet.</p>
            </div>
          )}
          {types.map((t) => {
            const L = LOCATION_LABELS[t.location_type] || LOCATION_LABELS.google_meet;
            const Icon = L.icon;
            return (
              <div key={t.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-4 flex flex-col gap-3"
                   data-testid={`apt-type-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background: t.color || "#E8712C"}} />
                      <h3 className="font-semibold text-base truncate">{t.name}</h3>
                    </div>
                    <p className="text-xs text-[#57534E] mt-0.5 line-clamp-2">{t.description}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.active ? "bg-[#E8F0E4] text-[#2C3A29]" : "bg-[#F5F5F4] text-[#A8A29E]"}`}>
                      {t.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#57534E]">
                  <span>{t.duration_minutes} min</span>
                  <span className="inline-flex items-center gap-1"><Icon size={12} /> {L.label.replace(" — Automatic","").replace(" — Manual Link","")}</span>
                  <span className="capitalize">{t.visibility}</span>
                </div>

                <div className="flex items-center gap-1 flex-wrap pt-1">
                  <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`apt-edit-${t.id}`}
                          className="rounded-full h-8"><Pencil size={12} className="mr-1" /> Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => copyLink(t)} data-testid={`apt-copy-${t.id}`}
                          className="rounded-full h-8"><Copy size={12} className="mr-1" /> Copy link</Button>
                  <div className="relative group">
                    <Button variant="outline" size="sm" className="rounded-full h-8" data-testid={`apt-more-${t.id}`}>
                      <MoreVertical size={12} />
                    </Button>
                    <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-white border border-[#E8E2D9] rounded-xl shadow-lg z-10 min-w-[160px] p-1">
                      <button onClick={() => duplicate(t)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#F5F5F4] rounded-lg"
                              data-testid={`apt-dup-${t.id}`}>Duplicate</button>
                      <button onClick={() => toggleActive(t)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#F5F5F4] rounded-lg"
                              data-testid={`apt-toggle-${t.id}`}>{t.active ? "Deactivate" : "Activate"}</button>
                      <button onClick={() => remove(t)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-[#F5F5F4] rounded-lg text-[#B02222]"
                              data-testid={`apt-del-${t.id}`}>Delete</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editId ? "Edit" : "Create"} Appointment Type</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                     placeholder="Discovery Call" className="mt-1.5" data-testid="apt-form-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                        placeholder="A 30-minute intro call to understand your needs." className="mt-1.5"
                        data-testid="apt-form-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Duration (minutes)</Label>
                <Input type="number" min={5} max={480} value={form.duration_minutes}
                       onChange={(e) => setForm({...form, duration_minutes: Number(e.target.value) || 30})}
                       className="mt-1.5" data-testid="apt-form-duration" />
              </div>
              <div>
                <Label>Color</Label>
                <Input type="color" value={form.color} onChange={(e) => setForm({...form, color: e.target.value})}
                       className="mt-1.5 h-10" data-testid="apt-form-color" />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Select value={form.location_type} onValueChange={(v) => setForm({...form, location_type: v})}>
                <SelectTrigger className="mt-1.5" data-testid="apt-form-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="google_meet">Google Meet — Automatic</SelectItem>
                  <SelectItem value="teams">Microsoft Teams — Automatic</SelectItem>
                  <SelectItem value="zoom_manual">Zoom — Manual Link</SelectItem>
                  <SelectItem value="phone">Phone Call</SelectItem>
                  <SelectItem value="in_person">In Person</SelectItem>
                  <SelectItem value="custom_link">Custom Link</SelectItem>
                </SelectContent>
              </Select>

              {/* Teams capability guard — clients must not book a Teams
                  appointment that can't generate a join URL. */}
              {form.location_type === "teams" && caps.loaded && !caps.teams_capable && (
                <div className="mt-2 rounded-2xl border border-[#F5C9A6] bg-[#FDE9D8] p-3 flex gap-2.5" data-testid="teams-guard-warning">
                  <AlertTriangle size={16} className="text-[#B85C1F] flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-[#5A3416] leading-relaxed">
                    <div className="font-semibold text-[#B85C1F] mb-1">Microsoft Teams requires a Microsoft 365 work/school account for automatic meeting creation.</div>
                    <div>{caps.outlook_connected
                      ? "Your connected Outlook is a personal Microsoft account, which cannot generate Teams links via delegated Graph."
                      : "No Microsoft account is connected yet."}</div>
                    <div className="mt-2 font-medium">Choose a fallback that will actually deliver a working meeting link:</div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {caps.google_connected && (
                        <button type="button" onClick={() => setForm({...form, location_type: "google_meet"})}
                          data-testid="teams-fallback-google-meet"
                          className="rounded-full bg-white border border-[#F5C9A6] px-3 py-1 text-xs font-medium hover:bg-[#FDE9D8]">
                          Use Google Meet
                        </button>
                      )}
                      <button type="button" onClick={() => setForm({...form, location_type: "zoom_manual"})}
                        data-testid="teams-fallback-zoom"
                        className="rounded-full bg-white border border-[#F5C9A6] px-3 py-1 text-xs font-medium hover:bg-[#FDE9D8]">
                        Zoom manual link
                      </button>
                      <button type="button" onClick={() => setForm({...form, location_type: "custom_link"})}
                        data-testid="teams-fallback-custom"
                        className="rounded-full bg-white border border-[#F5C9A6] px-3 py-1 text-xs font-medium hover:bg-[#FDE9D8]">
                        Custom link
                      </button>
                      <button type="button" onClick={() => setForm({...form, location_type: "phone"})}
                        data-testid="teams-fallback-phone"
                        className="rounded-full bg-white border border-[#F5C9A6] px-3 py-1 text-xs font-medium hover:bg-[#FDE9D8]">
                        Phone call
                      </button>
                    </div>
                    <div className="mt-2 text-[10px] text-[#8A6543]">
                      To enable Teams automatically, connect a Microsoft 365 work or school account in Settings → Calendar Sync.
                    </div>
                  </div>
                </div>
              )}

              {(form.location_type === "zoom_manual" || form.location_type === "custom_link" ||
                form.location_type === "in_person" || form.location_type === "phone") && (
                <Input value={form.location_details} onChange={(e) => setForm({...form, location_details: e.target.value})}
                       placeholder={form.location_type === "zoom_manual" ? "https://zoom.us/j/..." :
                                    form.location_type === "custom_link" ? "https://..." :
                                    form.location_type === "phone" ? "Client will call +1-555..." :
                                    "Office address"}
                       className="mt-2" data-testid="apt-form-location-details" />
              )}
            </div>

            <details className="border border-[#E8E2D9] rounded-2xl p-3">
              <summary className="cursor-pointer text-sm font-medium">Client questions ({(form.custom_questions || []).length})</summary>
              <div className="space-y-2 mt-3">
                {(form.custom_questions || []).map((q, idx) => (
                  <div key={q.key || `q-${idx}`} className="border border-[#E8E2D9] rounded-xl p-2 space-y-2"
                       data-testid={`apt-q-${idx}`}>
                    <div className="flex items-center gap-2">
                      <Input placeholder="Question label" value={q.label || ""}
                             onChange={(e) => setForm({...form, custom_questions: form.custom_questions.map((x,i) => i===idx?{...x,label:e.target.value,key:x.key||_slug(e.target.value)}:x)})}
                             data-testid={`apt-q-label-${idx}`} className="flex-1" />
                      <Select value={q.type || "short_text"}
                              onValueChange={(v) => setForm({...form, custom_questions: form.custom_questions.map((x,i) => i===idx?{...x,type:v}:x)})}>
                        <SelectTrigger className="w-40" data-testid={`apt-q-type-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="short_text">Short text</SelectItem>
                          <SelectItem value="long_text">Long text</SelectItem>
                          <SelectItem value="dropdown">Dropdown</SelectItem>
                          <SelectItem value="multiple_choice">Multiple choice</SelectItem>
                          <SelectItem value="checkbox">Checkbox</SelectItem>
                          <SelectItem value="yes_no">Yes / No</SelectItem>
                        </SelectContent>
                      </Select>
                      <button onClick={() => idx > 0 && setForm({...form, custom_questions: swap(form.custom_questions, idx, idx-1)})}
                              className="p-1 rounded-lg hover:bg-[#F5F5F4]" data-testid={`apt-q-up-${idx}`}>
                        <ArrowUp size={12} />
                      </button>
                      <button onClick={() => idx < form.custom_questions.length-1 && setForm({...form, custom_questions: swap(form.custom_questions, idx, idx+1)})}
                              className="p-1 rounded-lg hover:bg-[#F5F5F4]" data-testid={`apt-q-down-${idx}`}>
                        <ArrowDown size={12} />
                      </button>
                      <button onClick={() => setForm({...form, custom_questions: form.custom_questions.filter((_,i) => i!==idx)})}
                              className="p-1.5 rounded-lg hover:bg-[#F5F5F4] text-[#B02222]" data-testid={`apt-q-del-${idx}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                    {["dropdown","multiple_choice","checkbox"].includes(q.type) && (
                      <Input placeholder="Options — comma-separated (e.g. Option A, Option B)"
                             value={(q.options || []).join(", ")}
                             onChange={(e) => setForm({...form, custom_questions: form.custom_questions.map((x,i) => i===idx?{...x,options:e.target.value.split(",").map(s=>s.trim()).filter(Boolean)}:x)})}
                             data-testid={`apt-q-options-${idx}`} />
                    )}
                    <label className="text-xs flex items-center gap-1.5">
                      <input type="checkbox" checked={!!q.required}
                             onChange={(e) => setForm({...form, custom_questions: form.custom_questions.map((x,i) => i===idx?{...x,required:e.target.checked}:x)})}
                             data-testid={`apt-q-req-${idx}`} />
                      Required
                    </label>
                  </div>
                ))}
                <button onClick={() => setForm({...form, custom_questions: [...(form.custom_questions || []), {label:"", type:"short_text", key:`q${(form.custom_questions||[]).length+1}`, required:false}]})}
                        className="text-xs px-2 py-1 rounded-lg border border-[#E8E2D9]" data-testid="apt-q-add">
                  + Add question
                </button>
              </div>
            </details>

            {/* AVAILABILITY SOURCE — Standard (inherit org weekly hours) or Custom for this type. */}
            <div className="border border-[#E8E2D9] rounded-2xl p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <Label className="text-sm font-medium">Availability</Label>
                  <p className="text-xs text-[#57534E] mt-0.5">
                    {form.availability_mode === "custom"
                      ? "Custom for this appointment type — set your own weekly hours below."
                      : "Standard Weekly Hours — this appointment type uses your organization's Weekly Hours."}
                  </p>
                </div>
                <label className="flex items-center gap-2 text-sm shrink-0">
                  <Switch
                    checked={form.availability_mode === "custom"}
                    onCheckedChange={(v) => setForm({
                      ...form,
                      availability_mode: v ? "custom" : "inherit",
                      custom_weekly_rules: v ? normalizeWeekly(form.custom_weekly_rules) : form.custom_weekly_rules,
                    })}
                    data-testid="apt-form-availability-mode" />
                  Use custom availability
                </label>
              </div>
              {form.availability_mode === "custom" && (
                <div className="mt-3 space-y-2" data-testid="apt-form-custom-availability">
                  {WEEKDAYS.map((d) => {
                    const ranges = (form.custom_weekly_rules?.[d.key]) || [];
                    const on = ranges.length > 0;
                    const rules = normalizeWeekly(form.custom_weekly_rules);
                    const setDay = (list) => setForm({ ...form, custom_weekly_rules: { ...rules, [d.key]: list } });
                    return (
                      <div key={d.key} className="flex items-start gap-3 border border-[#E8E2D9] rounded-xl p-2.5 flex-wrap"
                           data-testid={`apt-avail-day-${d.key}`}>
                        <label className="flex items-center gap-2 min-w-[110px] text-sm">
                          <Switch checked={on}
                                  onCheckedChange={(v) => setDay(v ? [{ start: "09:00", end: "17:00" }] : [])}
                                  data-testid={`apt-avail-toggle-${d.key}`} />
                          <span className={on ? "" : "text-[#A8A29E]"}>{d.label}</span>
                        </label>
                        <div className="flex-1 min-w-[220px] space-y-1.5">
                          {!on && <div className="text-xs text-[#A8A29E]">Unavailable</div>}
                          {on && ranges.map((rg, idx) => (
                            <div key={idx} className="flex items-center gap-2 flex-wrap">
                              <Input type="time" value={rg.start}
                                     onChange={(e) => setDay(ranges.map((r, i) => i === idx ? { ...r, start: e.target.value } : r))}
                                     className="w-28" data-testid={`apt-avail-start-${d.key}-${idx}`} />
                              <span className="text-xs text-[#A8A29E]">to</span>
                              <Input type="time" value={rg.end}
                                     onChange={(e) => setDay(ranges.map((r, i) => i === idx ? { ...r, end: e.target.value } : r))}
                                     className="w-28" data-testid={`apt-avail-end-${d.key}-${idx}`} />
                              <button onClick={() => setDay(ranges.filter((_, i) => i !== idx))}
                                      data-testid={`apt-avail-remove-${d.key}-${idx}`}
                                      className="p-1.5 rounded-lg text-[#B02222] hover:bg-[#F5F5F4]">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))}
                          {on && (
                            <button onClick={() => setDay([...ranges, { start: "13:00", end: "17:00" }])}
                                    data-testid={`apt-avail-add-range-${d.key}`}
                                    className="text-xs px-2 py-1 rounded-lg border border-[#E8E2D9] hover:border-[#E8712C]">
                              + Add range
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-[10px] text-[#A8A29E]">Date overrides and connected-calendar conflicts still apply on top of these hours.</p>
                </div>
              )}
            </div>

            <details className="border border-[#E8E2D9] rounded-2xl p-3">
              <summary className="cursor-pointer text-sm font-medium">Buffer time (override defaults)</summary>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div><Label>Before (min)</Label>
                  <Input type="number" min={0} value={form.buffer_before ?? ""} placeholder="Use global"
                         onChange={(e) => setForm({...form, buffer_before: e.target.value === "" ? null : Number(e.target.value)})}
                         className="mt-1.5" data-testid="apt-form-buf-before" /></div>
                <div><Label>After (min)</Label>
                  <Input type="number" min={0} value={form.buffer_after ?? ""} placeholder="Use global"
                         onChange={(e) => setForm({...form, buffer_after: e.target.value === "" ? null : Number(e.target.value)})}
                         className="mt-1.5" data-testid="apt-form-buf-after" /></div>
              </div>
            </details>

            <details className="border border-[#E8E2D9] rounded-2xl p-3">
              <summary className="cursor-pointer text-sm font-medium">Booking rules (override defaults)</summary>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div><Label>Min notice (min)</Label>
                  <Input type="number" min={0} value={form.min_notice_minutes ?? ""} placeholder="Use global"
                         onChange={(e) => setForm({...form, min_notice_minutes: e.target.value === "" ? null : Number(e.target.value)})}
                         className="mt-1.5" data-testid="apt-form-min-notice" /></div>
                <div><Label>Max advance (days)</Label>
                  <Input type="number" min={0} value={form.max_advance_days ?? ""} placeholder="Use global"
                         onChange={(e) => setForm({...form, max_advance_days: e.target.value === "" ? null : Number(e.target.value)})}
                         className="mt-1.5" data-testid="apt-form-max-advance" /></div>
                <div><Label>Slot increment (min)</Label>
                  <Input type="number" min={5} value={form.increment_minutes ?? ""} placeholder="Use global"
                         onChange={(e) => setForm({...form, increment_minutes: e.target.value === "" ? null : Number(e.target.value)})}
                         className="mt-1.5" data-testid="apt-form-increment" /></div>
                <div><Label>Daily limit</Label>
                  <Input type="number" min={0} value={form.daily_limit ?? ""} placeholder="Unlimited"
                         onChange={(e) => setForm({...form, daily_limit: e.target.value === "" ? null : Number(e.target.value)})}
                         className="mt-1.5" data-testid="apt-form-daily-limit" /></div>
              </div>
            </details>

            <div className="flex items-center justify-between border-t border-[#E8E2D9] pt-3">
              <div className="flex items-center gap-3">
                <label className="text-sm flex items-center gap-2">
                  <Switch checked={form.auto_confirm} onCheckedChange={(v) => setForm({...form, auto_confirm: v})}
                          data-testid="apt-form-autoconfirm" /> Auto-confirm bookings
                </label>
              </div>
              <div>
                <Label className="text-xs">Visibility</Label>
                <Select value={form.visibility} onValueChange={(v) => setForm({...form, visibility: v})}>
                  <SelectTrigger className="w-40 mt-1" data-testid="apt-form-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="portal">Portal only</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} data-testid="apt-form-save" className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
              {editId ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
